import React, { useState } from 'react';
import { NAV_ITEMS } from '../data/mockData';
import { ThemeColorPicker } from './ThemeColorPicker';
import { Settings, ChevronDown, LogOut, PanelLeftIcon, PanelLeftCloseIcon, Menu, X } from 'lucide-react';
interface SidebarProps {
  activeTab: string;
  setActiveTab: (id: string) => void;
  themeColor: string;
  setThemeColor: (color: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}
export function Sidebar({
  activeTab,
  setActiveTab,
  themeColor,
  setThemeColor,
  collapsed,
  onToggleCollapse
}: SidebarProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  return <>
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white z-40 flex items-center justify-between px-4 border-b border-gray-200" data-id="element-163">
        <div className="flex items-center gap-2" data-id="element-164">
          <img src="https://cdn.magicpatterns.com/uploads/pm1VedacPGtL1Qphc9edds/358347914_1264171364489162_5443488880451904890_n.jpg" alt="SSC Logo" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" data-id="element-165" />
          <h1 className="font-bold text-gray-900 tracking-tight text-[15px]" data-id="element-166">
            Sri Sakthi Cinemas
          </h1>
        </div>
        <button onClick={() => setMobileOpen(true)} className="p-2 -mr-2 text-gray-600 hover:text-gray-900" aria-label="Open menu" data-id="element-167">
          <Menu className="w-6 h-6" data-id="element-168" />
        </button>
      </header>

      {/* Mobile Drawer Overlay */}
      {mobileOpen && <div className="md:hidden fixed inset-0 bg-black/50 z-50 transition-opacity" onClick={() => setMobileOpen(false)} data-id="element-169" />}

      {/* Mobile Drawer Panel */}
      <aside className={`md:hidden fixed inset-y-0 left-0 w-72 bg-[#1A1A1A] text-gray-300 z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-170">
        {/* Header with logo and close */}
        <div className="p-6 pt-[24px] pb-[24px] pl-[12px] pr-[12px]" data-id="element-171">
          <div className="flex items-center justify-between" data-id="element-172">
            <div className="flex items-center gap-3" data-id="element-173">
              <img src="https://cdn.magicpatterns.com/uploads/pm1VedacPGtL1Qphc9edds/358347914_1264171364489162_5443488880451904890_n.jpg" alt="SSC Logo" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" data-id="element-174" />
              <div className="overflow-hidden" data-id="element-175">
                <h1 className="font-bold text-white tracking-tight whitespace-nowrap text-[16px]" data-id="element-176">
                  Sri Sakthi Cinemas
                </h1>
                <p className="text-xs text-gray-500 mt-0.5 whitespace-nowrap" data-id="element-177">
                  Theatre Operations
                </p>
              </div>
            </div>
            <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-md hover:bg-white/10 transition-colors text-gray-500 hover:text-gray-300" aria-label="Close menu" data-id="element-178">
              <X className="w-5 h-5" data-id="element-179" />
            </button>
          </div>
        </div>

        <nav className="flex-1 py-4 space-y-1 overflow-y-auto px-3" data-id="element-180">
          {NAV_ITEMS.map(item => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return <button key={item.id} onClick={() => {
            setActiveTab(item.id);
            setMobileOpen(false);
          }} className={`w-full flex items-center rounded-lg transition-colors text-sm font-medium gap-3 px-3 py-2.5 ${isActive ? 'bg-white/10 text-white' : 'hover:bg-white/5 hover:text-white'}`} data-id="element-181">
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-accent' : 'text-gray-400'}`} style={isActive ? {
              color: 'var(--accent-color)'
            } : {}} data-id="element-182" />
                <span className="whitespace-nowrap overflow-hidden" data-id="element-183">
                  {item.label}
                </span>
              </button>;
        })}

          {/* Settings with expandable theme picker */}
          <div className="pt-2" data-id="element-184">
            <button onClick={() => setSettingsOpen(!settingsOpen)} className={`w-full flex items-center rounded-lg transition-colors text-sm font-medium gap-3 px-3 py-2.5 ${settingsOpen ? 'bg-white/10 text-white' : 'hover:bg-white/5 hover:text-white'}`} data-id="element-185">
              <Settings className={`w-5 h-5 flex-shrink-0 ${settingsOpen ? '' : 'text-gray-400'}`} style={settingsOpen ? {
              color: 'var(--accent-color)'
            } : {}} data-id="element-186" />
              <span className="whitespace-nowrap overflow-hidden" data-id="element-187">
                Settings
              </span>
              <ChevronDown className={`w-4 h-4 ml-auto text-gray-500 transition-transform duration-200 ${settingsOpen ? 'rotate-180' : ''}`} data-id="element-188" />
            </button>

            <div className={`overflow-hidden transition-all duration-200 ease-in-out ${settingsOpen ? 'max-h-40 opacity-100 mt-1' : 'max-h-0 opacity-0'}`} data-id="element-189">
              <div className="ml-2 pl-6 border-l border-white/10" data-id="element-190">
                <ThemeColorPicker currentColor={themeColor} onChange={setThemeColor} data-id="element-191" />
              </div>
            </div>
          </div>
        </nav>

        {/* Profile section */}
        <div className="border-t border-white/10" data-id="element-192">
          <div className="px-5 pt-3 pb-1" data-id="element-193">
            <p className="text-[11px] text-gray-600 tracking-wide text-center" data-id="element-194">
              Powered by{' '}
              <span className="text-gray-500 font-medium" data-id="element-195">Houseful</span>
            </p>
          </div>
          <div className="py-4 flex items-center gap-3 px-4" data-id="element-196">
            <img src="https://cdn.magicpatterns.com/uploads/cxLboMRThjrug3qgz9FsCL/39b9acb2-dfc4-4dfc-b30c-255d5de4100a.jpg" alt="Anjali R" className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-197" />
            <div className="flex-1 min-w-0" data-id="element-198">
              <p className="text-sm font-medium text-white truncate" data-id="element-199">
                Anjali R
              </p>
              <p className="text-[11px] text-gray-500 truncate" data-id="element-200">Premium Plan</p>
            </div>
            <button className="p-1.5 rounded-md hover:bg-white/5 transition-colors text-gray-500 hover:text-gray-300" data-id="element-201">
              <LogOut className="w-4 h-4" data-id="element-202" />
            </button>
          </div>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col h-screen fixed left-0 top-0 bg-[#1A1A1A] text-gray-300 z-50 transition-all duration-300 ease-in-out ${collapsed ? 'w-16' : 'w-64'}`} data-id="element-203">
        {/* Header with logo and toggle */}
        <div className={`${`p-4 ${collapsed ? 'px-2' : 'p-6'}`} pt-[24px] pb-[24px] pl-[12px] pr-[12px]`} data-id="element-204">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`} data-id="element-205">
            <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`} data-id="element-206">
              <img src="https://cdn.magicpatterns.com/uploads/pm1VedacPGtL1Qphc9edds/358347914_1264171364489162_5443488880451904890_n.jpg" alt="SSC Logo" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" data-id="element-207" />
              {!collapsed && <div className="overflow-hidden" data-id="element-208">
                  <h1 className="font-bold text-white tracking-tight whitespace-nowrap text-[16px]" data-id="element-209">
                    Sri Sakthi Cinemas
                  </h1>
                  <p className="text-xs text-gray-500 mt-0.5 whitespace-nowrap" data-id="element-210">
                    Theatre Operations
                  </p>
                </div>}
            </div>
            {!collapsed && <button onClick={onToggleCollapse} className="p-1.5 rounded-md hover:bg-white/10 transition-colors text-gray-500 hover:text-gray-300" aria-label="Collapse sidebar" data-id="element-211">
                <PanelLeftCloseIcon className="w-4 h-4" data-id="element-212" />
              </button>}
          </div>
          {collapsed && <button onClick={onToggleCollapse} className="w-full mt-3 p-1.5 rounded-md hover:bg-white/10 transition-colors text-gray-500 hover:text-gray-300 flex justify-center" aria-label="Expand sidebar" data-id="element-213">
              <PanelLeftIcon className="w-4 h-4" data-id="element-214" />
            </button>}
        </div>

        <nav className={`flex-1 py-4 space-y-1 overflow-y-auto ${collapsed ? 'px-2' : 'px-3'}`} data-id="element-215">
          {NAV_ITEMS.map(item => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return <button key={item.id} onClick={() => setActiveTab(item.id)} title={collapsed ? item.label : undefined} className={`w-full flex items-center rounded-lg transition-colors text-sm font-medium ${collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'} ${isActive ? 'bg-white/10 text-white' : 'hover:bg-white/5 hover:text-white'}`} data-id="element-216">
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-accent' : 'text-gray-400'}`} style={isActive ? {
              color: 'var(--accent-color)'
            } : {}} data-id="element-217" />
                {!collapsed && <span className="whitespace-nowrap overflow-hidden" data-id="element-218">
                    {item.label}
                  </span>}
              </button>;
        })}

          {/* Settings with expandable theme picker */}
          <div className="pt-2" data-id="element-219">
            <button onClick={() => !collapsed && setSettingsOpen(!settingsOpen)} title={collapsed ? 'Settings' : undefined} className={`w-full flex items-center rounded-lg transition-colors text-sm font-medium ${collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'} ${settingsOpen && !collapsed ? 'bg-white/10 text-white' : 'hover:bg-white/5 hover:text-white'}`} data-id="element-220">
              <Settings className={`w-5 h-5 flex-shrink-0 ${settingsOpen && !collapsed ? '' : 'text-gray-400'}`} style={settingsOpen && !collapsed ? {
              color: 'var(--accent-color)'
            } : {}} data-id="element-221" />
              {!collapsed && <>
                  <span className="whitespace-nowrap overflow-hidden" data-id="element-222">
                    Settings
                  </span>
                  <ChevronDown className={`w-4 h-4 ml-auto text-gray-500 transition-transform duration-200 ${settingsOpen ? 'rotate-180' : ''}`} data-id="element-223" />
                </>}
            </button>

            {!collapsed && <div className={`overflow-hidden transition-all duration-200 ease-in-out ${settingsOpen ? 'max-h-40 opacity-100 mt-1' : 'max-h-0 opacity-0'}`} data-id="element-224">
                <div className="ml-2 pl-6 border-l border-white/10" data-id="element-225">
                  <ThemeColorPicker currentColor={themeColor} onChange={setThemeColor} data-id="element-226" />
                </div>
              </div>}
          </div>
        </nav>

        {/* Profile section */}
        <div className="border-t border-white/10" data-id="element-227">
          {!collapsed && <div className="px-5 pt-3 pb-1" data-id="element-228">
              <p className="text-[11px] text-gray-600 tracking-wide text-center" data-id="element-229">
                Powered by{' '}
                <span className="text-gray-500 font-medium" data-id="element-230">Houseful</span>
              </p>
            </div>}
          <div className={`py-4 flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-4'}`} data-id="element-231">
            <img src="https://cdn.magicpatterns.com/uploads/cxLboMRThjrug3qgz9FsCL/39b9acb2-dfc4-4dfc-b30c-255d5de4100a.jpg" alt="Anjali R" className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-232" />
            {!collapsed && <>
                <div className="flex-1 min-w-0" data-id="element-233">
                  <p className="text-sm font-medium text-white truncate" data-id="element-234">
                    Anjali R
                  </p>
                  <p className="text-[11px] text-gray-500 truncate" data-id="element-235">
                    Premium Plan
                  </p>
                </div>
                <button className="p-1.5 rounded-md hover:bg-white/5 transition-colors text-gray-500 hover:text-gray-300" data-id="element-236">
                  <LogOut className="w-4 h-4" data-id="element-237" />
                </button>
              </>}
          </div>
        </div>
      </aside>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-safe" data-id="element-238">
        <div className="flex justify-around items-center h-16 px-2" data-id="element-239">
          {NAV_ITEMS.filter(item => item.id !== 'staff').slice(0, 4).map(item => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return <button key={item.id} onClick={() => setActiveTab(item.id)} className="flex flex-col items-center justify-center w-full h-full space-y-1" data-id="element-240">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-accent' : 'text-gray-400'}`} style={isActive ? {
              color: 'var(--accent-color)'
            } : {}} data-id="element-241" />
                  <span className={`text-[10px] font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-242">
                    {item.label.split(' ')[0]}
                  </span>
                </button>;
        })}
        </div>
      </nav>
    </>;
}